import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Tabs, TextInput } from '@/components/ui/daisy';
import { AnswerMode } from '@/data/hook/question/answers/useAnswerMode';
import { QuestionCreateDTO } from '@/models/Answer';
import { TrashIcon } from '@heroicons/react/24/outline';
import QuestionColumnItemMedia from './QuestionColumnItemMedia';
import QuestionColumnItemText from './QuestionColumnItemText';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import PedWorkspaceGuard from '@/components/shared/PedWorkspaceGuard';
import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';
import { twMerge } from 'tailwind-merge';

type QuestionColumnItemProps = {
  item: QuestionCreateDTO;
  index: number;
  autoFocus?: boolean;
  onUpdateText: (text: string) => void;
  onUpdateMedia: (file: File | string, altText?: string) => void;
  onDelete: () => void;
};

export default function QuestionColumnItem({
  item,
  index,
  autoFocus,
  onUpdateText,
  onUpdateMedia,
  onDelete,
}: QuestionColumnItemProps) {
  const { t } = useTranslation('translation', {
    keyPrefix:
      'courseLineForm.questionsTab.createQuestionModal.combinationForm',
  });
  const { t: tForm } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.questionForm',
  });

  const [mode, setMode] = useState<AnswerMode>(
    item.mediaFile || item.mediaLink ? 'media' : 'text',
  );

  const label = t('itemLabel', { number: String(index + 1).padStart(2, '0') });
  const { isLimitedEdition } = usePedWorkspace();

  return (
    <div className="flex gap-3 flex-1 items-end">
      <TextInput.Fieldset label={label} className="flex-1">
        <ConditionalRenderer
          condition={mode === 'text'}
          fallback={
            <QuestionColumnItemMedia
              item={item}
              onUpdateMedia={onUpdateMedia}
            />
          }
        >
          <QuestionColumnItemText
            item={item}
            autoFocus={autoFocus}
            onUpdateText={onUpdateText}
          />
        </ConditionalRenderer>
      </TextInput.Fieldset>

      <div className="flex gap-3 items-center p-1 justify-end shrink-0 w-fit">
        <PedWorkspaceGuard>
          <Tabs
            size="sm"
            variant="box"
            value={mode}
            onChange={value => setMode(value as AnswerMode)}
          >
            <Tabs.Tab value="text" disabled={isLimitedEdition}>
              {t('tabText')}
            </Tabs.Tab>
            <Tabs.Tab value="media" disabled={isLimitedEdition}>
              {t('tabMedia')}
            </Tabs.Tab>
          </Tabs>
        </PedWorkspaceGuard>

        <PedWorkspaceGuard>
          <Button
            type="button"
            variant="ghost"
            shape="square"
            onClick={onDelete}
            aria-label={tForm('delete')}
            disabled={isLimitedEdition}
            className="text-error"
          >
            <TrashIcon className={twMerge('size-5 ')} />
          </Button>
        </PedWorkspaceGuard>
      </div>
    </div>
  );
}
